// tools for root user
import axios from "axios";
import { useState, lazy, Suspense } from "react";

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
const Chip = lazy(() => import('@mui/material/Chip'));
const Button = lazy(() => import('@/components/Button'));

import { styleCards } from '@/utils/Styles';
import { getErrorMessage } from '@/utils/Errors';

const SearchUser = lazy(() => import('@/components/Root/SearchUser'));
const CreateUser = lazy(() => import('@/components/Root/CreateUser'));
const UpdateUser = lazy(() => import('@/components/Root/UpdateUser'));
const CopyTables = lazy(() => import('@/components/Root/CopyTables'));
const UploadFiles = lazy(() => import('@/components/Root/UploadFiles'));

const BooleanModal = lazy(() => import('/src/modals/BooleanModal'));

export default function Tools({t,language,apiUrl,token,user,sendMessage,handleLogin,fullScreen,openUrlModal}){

	const { sxCard } = styleCards();

	const [ currentAction, setCurrentAction ] = useState(null);
	const [ rootAnswer, setRootAnswer ] = useState(null);
    const [ rootActions ] = useState(['clear','search_user','create_user','update_user','copy_tables','upload_files']);

    // modal to confirm deletion
    const [ deleteUserID, setDeleteUserID ] = useState(false);
    const [ showConfirmDeleteUser, setShowConfirmDeleteUser ] = useState(false);
    const [ titleDeleteUser ] = useState(t('actions.delete.title'));
    const [ textDeleteUser ] = useState(t('actions.delete.confirm'));

    // confirm deletion
    const confirmDeleteUser = (id) => {   
        setDeleteUserID(id);
        setShowConfirmDeleteUser(true);
    }

    // if confirming deletion
    const handleConfirmDeleteUser = (e,answer) => {

        // close confirmation modal
        setShowConfirmDeleteUser(false);

        // if yes, delete
        if(answer){    
                
            const apiPath = '/users/delete';            

            var errorMessage = t('root.delete_user.errors.generic');

            const config = {
                headers: { Authorization: `Bearer ${token}` },
                data: {
                    language: language,
                    companyuser: user?.company,
                    ids: deleteUserID
                }
            };

            axios.delete(apiUrl+apiPath,config)            
            .then(function (response) {

                if(response?.data){
              
                    const responseData = response.data;                

                    // if result is ok (1)
                    if(responseData?.result){
                        sendMessage(t('root.delete_user.success'));
                        setRootAnswer(null);
                    } 
                    else{
                        if(responseData?.status && responseData.status === 401){
                            handleLogin();                        
                        }
                        else{
                            // check error message
                            if(responseData?.data?.reasons){      
                                errorMessage = getErrorMessage(t,'root.delete_user',responseData?.data?.reasons);
                            }
                            
                            sendMessage(errorMessage,'error');
                        }                        
                    }
                }
                else{                
                    sendMessage(errorMessage,'error');                    
                }
            })
            .catch(function (error){
                
                if(error?.status && error.status === 401){
                    handleLogin();                        
                }
                else{                        
                    sendMessage(errorMessage,'error');
                }
            });
        }
    }

    const handleRootButton = (action) => {

        //sendMessage(action);

        setCurrentAction(action);

        switch(action){

            case 'clear':
                setRootAnswer(null);
                break;

            case 'search_user':            	
                setRootAnswer(                   
                	<SearchUser 
            			t={t} 
            			language={language}
            			user={user}
            			apiUrl={apiUrl}
            			token={token}	                			
            			sendMessage={sendMessage}
            			setRootAnswer={setRootAnswer}
            			handleLogin={handleLogin}
                        confirmDelete={confirmDeleteUser}
            		/>
                );
                break;

            case 'create_user':             
                setRootAnswer(
                    <CreateUser 
                        t={t} 
                        language={language}
                        user={user}
                        apiUrl={apiUrl}
                        token={token}                               
                        sendMessage={sendMessage}
                        setRootAnswer={setRootAnswer}
                        handleLogin={handleLogin}
                    />
                );
                break;

            case 'update_user':             
                setRootAnswer(                    
                    <UpdateUser 
                        t={t} 
                        language={language}
                        user={user}
                        apiUrl={apiUrl}
                        token={token}                               
                        sendMessage={sendMessage}
                        setRootAnswer={setRootAnswer}
                        handleLogin={handleLogin}
                        confirmDelete={confirmDeleteUser}
                    />
                );
                break;

            case 'copy_tables':             
                setRootAnswer(                    
                    <CopyTables 
                        t={t} 
                        language={language}
                        user={user}
                        apiUrl={apiUrl}
                        token={token}                               
                        sendMessage={sendMessage}
                        setRootAnswer={setRootAnswer}
                        handleLogin={handleLogin}                        
                    />
                );
                break;

            case 'upload_files':             
                setRootAnswer(                    
                    <UploadFiles 
                        t={t} 
                        language={language}
                        user={user}
                        apiUrl={apiUrl}
                        token={token}                               
                        sendMessage={sendMessage}
                        setRootAnswer={setRootAnswer}
                        handleLogin={handleLogin}         
                        openUrlModal={openUrlModal}               
                    />
                );
                break;
        }
    }

	return (
        
        user && user?.roles_tags?.includes('root') && rootActions && rootActions?.length > 0 &&
        <>
        {
            showConfirmDeleteUser &&
            <Suspense>
                <BooleanModal 
                    open={showConfirmDeleteUser} 
                    setOpen={setShowConfirmDeleteUser} 
                    title={titleDeleteUser}
                    text={textDeleteUser}
                    handleAnswer={handleConfirmDeleteUser}
                    fullScreen={fullScreen}
                />
            </Suspense>
        }
        <Suspense>
            <Card sx={{...sxCard,marginTop:2}}>
                <CardContent>                    
                    <div className="text-xl font-bold">
                        {t('root.tools.title')}
                    </div>                        
                    <div className="mt-4 flex justify-center grid grid-cols-12">                        
                        {
                            rootActions?.map((action) => (
                                <div 
                                	key={'root-action-'+action} 
                                	className='mx-1 md:col-span-3 col-span-6 mb-4 text-center'
                                >
                                    <Button                                         
                                        onClick={() => handleRootButton(action)}
                                        text={t('root.tools.'+action)}
                                        type='root'
                                        variant={currentAction === action && 'contained'}
                                    />
                                </div>
                            ))
                        }
                    </div>
                    {rootAnswer}
                                      
                </CardContent>
            </Card>
        </Suspense>
        </>
	);
}